// src/components/ReportsTable.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Checkbox,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useDispatch } from 'react-redux';
import { createReport, fetchChildrenWithReportStatus, sendGroupReports } from '../actions/reportActions';
import { updateGroupReport, fetchGroupReports } from '../actions/groupReportActions';

const REQUIRED_FIELDS = [
  { key: 'meal', label: 'Masa' },
  { key: 'eating_amount', label: 'Cât a mâncat' },
  { key: 'behaviour_mood', label: 'Comportament & Dispoziție' },
  { key: 'wc_visits', label: 'Vizite la WC' },
  { key: 'sleep_hours', label: 'Ore de Somn' },
];

const ReportsTable = ({ children, groupReports, date, mealOptions = [] }) => {
  const dispatch = useDispatch();
  const [groupFeedback, setGroupFeedback] = useState({});
  const [groupSendStatus, setGroupSendStatus] = useState({});
  const [groupSendLoading, setGroupSendLoading] = useState({});

  // Group children by group_id
  const childrenByGroup = useMemo(() => {
    if (!children || children.length === 0) return {};
    return children.reduce((acc, child) => {
      const groupId = child.group_id;
      if (!acc[groupId]) {
        acc[groupId] = { groupName: child.group_name, children: [] };
      }
      acc[groupId].children.push(child);
      return acc;
    }, {});
  }, [children]);

  const currentDay = useMemo(() => new Date(date).getDay(), [date]);
  const todaysMeals = useMemo(() => {
    if (!mealOptions || mealOptions.length === 0) {
      return [];
    }
    return mealOptions.filter((item) => item.day_of_week === currentDay);
  }, [mealOptions, currentDay]);

  // Group-level report state
  const [groupReportData, setGroupReportData] = useState({});
  useEffect(() => {
    const initialData = {};
    Object.keys(childrenByGroup).forEach((groupId) => {
      initialData[groupId] = {
        group_info: groupReports?.[groupId]?.group_info || '',
        photo_link: groupReports?.[groupId]?.photo_link || '',
      };
    });
    setGroupReportData(initialData);
  }, [groupReports, childrenByGroup]);

  const handleGroupReportChange = (groupId, value) => {
    setGroupReportData((prev) => ({
      ...prev,
      [groupId]: { ...prev[groupId], group_info: value },
    }));
  };

  const handleGroupPhotoChange = (groupId, value) => {
    setGroupReportData((prev) => ({
      ...prev,
      [groupId]: { ...prev[groupId], photo_link: value },
    }));
  };

  const handleGroupReportSave = (groupId) => {
    dispatch(
      updateGroupReport({
        group_id: groupId,
        date,
        group_info: groupReportData[groupId]?.group_info || '',
        photo_link: groupReportData[groupId]?.photo_link || '',
      })
    )
      .then(() => {
        setGroupFeedback((prev) => ({
          ...prev,
          [groupId]: { type: 'success', message: 'Informațiile Grupului au fost actualizate!' },
        }));
        dispatch(fetchGroupReports(date));
      })
      .catch((err) => {
        console.error('Error updating group report:', err);
        setGroupFeedback((prev) => ({
          ...prev,
          [groupId]: { type: 'error', message: 'Eroare la actualizarea informațiilor grupului.' },
        }));
      });
  };
  

  // Individual report state
  const [reportData, setReportData] = useState({});
  useEffect(() => {
    if (!children || children.length === 0) {
      setReportData({});
      return;
    }

    const defaultMealId = todaysMeals[0]?.meal_id || '';
    setReportData((prev) => {
      const nextData = { ...prev };
      const activeChildIds = new Set();

      children.forEach((child) => {
        activeChildIds.add(child.child_id);
        if (!nextData[child.child_id]) {
          nextData[child.child_id] = {
            present: true,
            meal: defaultMealId,
            eating_amount: '',
            behaviour_mood: '',
            wc_visits: '',
            sleep_hours: '',
            health_notes: '',
            special_events: '',
            areas_of_concern: '',
            parent_attention: '',
          };
        } else if (!nextData[child.child_id].meal && defaultMealId) {
          nextData[child.child_id].meal = defaultMealId;
        }
      });

      Object.keys(nextData).forEach((childId) => {
        if (!activeChildIds.has(Number(childId))) {
          delete nextData[childId];
        }
      });

      return nextData;
    });
  }, [children, todaysMeals]);

  const handleInputChange = (childId, field, value) => {
    setReportData((prev) => ({
      ...prev,
      [childId]: { ...prev[childId], [field]: value },
    }));
  };

  const getMissingRequiredFields = (childId) => {
    const childReport = reportData[childId];
    if (!childReport) {
      return REQUIRED_FIELDS.map((field) => field.label);
    }

    if (!childReport.present) {
      if (todaysMeals.length === 0) {
        return [];
      }
      return childReport.meal ? [] : ['Masa'];
    }

    return REQUIRED_FIELDS.filter((field) => {
      if (field.key === 'meal' && todaysMeals.length === 0) {
        return false;
      }

      const value = childReport[field.key];
      return value === undefined || value === null || value === '';
    }).map((field) => field.label);
  };

  const validateReport = (childId) => {
    return getMissingRequiredFields(childId).length === 0;
  };

  const getChildUiState = (child) => {
    if (child.reportSent) {
      return { label: 'Raport creat', color: 'success', buttonLabel: 'Raport Trimis' };
    }

    const childReport = reportData[child.child_id];
    if (!childReport?.present) {
      return { label: 'Absent - gata de salvat', color: 'info', buttonLabel: 'Salvează Absența' };
    }

    if (validateReport(child.child_id)) {
      return { label: 'Completat - nesalvat', color: 'warning', buttonLabel: 'Salvează Raportul' };
    }

    return { label: 'Raport incomplet', color: 'error', buttonLabel: 'Salvează Raportul' };
  };

  const handleSubmitReport = async (child) => {
    const { group_id } = child;
    const missingFields = getMissingRequiredFields(child.child_id);
    if (missingFields.length > 0) {
      setGroupFeedback((prev) => ({
        ...prev,
        [group_id]: {
          type: 'error',
          message: `Pentru ${child.name} lipsesc: ${missingFields.join(', ')}.`,
        },
      }));
      return;
    }
    setGroupFeedback((prev) => ({
      ...prev,
      [group_id]: undefined,
    }));
    const data = {
      child_id: child.child_id,
      date,
      ...reportData[child.child_id],
    };
    try {
      const savedReport = await dispatch(createReport(data));
      if (!savedReport?.report_id) {
        throw new Error('Răspuns invalid la salvarea raportului.');
      }

      await dispatch(fetchChildrenWithReportStatus());

      setGroupFeedback((prev) => ({
        ...prev,
        [group_id]: { type: 'success', message: `Raportul pentru ${child.name} a fost creat.` },
      }));
    } catch (error) {
      console.error('Eroare la crearea raportului:', error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'A apărut o eroare la salvarea raportului.';
      setGroupFeedback((prev) => ({
        ...prev,
        [group_id]: { type: 'error', message },
      }));
    }
  };

  const handleSendReports = (groupId) => {
    setGroupSendLoading((prev) => ({ ...prev, [groupId]: true }));
    setGroupSendStatus((prev) => ({ ...prev, [groupId]: null }));
    dispatch(sendGroupReports(groupId, date))
      .then((result) => {
        setGroupSendStatus((prev) => ({
          ...prev,
          [groupId]: {
            type: 'success',
            message: result?.message || `Au fost trimise ${result?.sentCount || 0} rapoarte.`,
          },
        }));
      })
      .catch((error) => {
        const message =
          error?.response?.data?.message || 'Nu s-au putut trimite rapoartele pentru acest grup.';
        setGroupSendStatus((prev) => ({
          ...prev,
          [groupId]: { type: 'error', message },
        }));
      })
      .finally(() => {
        setGroupSendLoading((prev) => ({ ...prev, [groupId]: false }));
      });
  };

  if (!children || children.length === 0) {
    return <Typography>Nu există copii de afișat.</Typography>;
  }

  return (
    <div>
      {Object.entries(childrenByGroup).map(([groupId, groupData]) => (
        <div key={groupId}>
          <Typography variant="h5" gutterBottom>
            Grupă: {groupData.groupName}
          </Typography>

          {groupFeedback[groupId]?.message && (
            <Alert severity={groupFeedback[groupId].type} sx={{ mb: 2 }}>
              {groupFeedback[groupId].message}
            </Alert>
          )}

          {/* Group-specific Information */}
          <TextField
            label="Informații Grupă"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={groupReportData[groupId]?.group_info || ''}
            onChange={(e) => handleGroupReportChange(groupId, e.target.value)}
          />
          <TextField
            label="Link Fotografie Generală"
            fullWidth
            margin="normal"
            value={groupReportData[groupId]?.photo_link || ''}
            onChange={(e) => handleGroupPhotoChange(groupId, e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleGroupReportSave(groupId)}
            style={{ marginBottom: '20px' }}
          >
            Salvează Informațiile Grupului
          </Button>

          {/* Accordion for Each Child */}
          {groupData.children.map((child) => {
            const isChildAbsent = !reportData[child.child_id]?.present;
            const areNonMealFieldsDisabled = child.reportSent || isChildAbsent;
            const areChildFieldsDisabled = child.reportSent;
            const isMealDisabled = child.reportSent || todaysMeals.length === 0;
            const childUiState = getChildUiState(child);

            return (
              <Accordion key={child.child_id}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`child-panel-${child.child_id}-content`}
                    id={`child-panel-${child.child_id}-header`}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                      <Typography>{child.name}</Typography>
                      <Chip label={childUiState.label} color={childUiState.color} size="small" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Present Checkbox */}
                      <Box display="flex" alignItems="center">
                        <Checkbox
                          checked={reportData[child.child_id]?.present || false}
                          onChange={(e) =>
                            handleInputChange(child.child_id, 'present', e.target.checked)
                          }
                          disabled={child.reportSent}
                        />
                        <Typography variant="body1">Prezent</Typography>
                      </Box>
                      {isChildAbsent && !child.reportSent && (
                        <Alert severity="info">
                          Copilul este marcat absent pentru această zi. Poți modifica doar câmpul
                          &nbsp;"Masa"; celelalte câmpuri sunt blocate.
                        </Alert>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          p: areChildFieldsDisabled ? 2 : 0,
                          borderRadius: 2,
                          backgroundColor: areChildFieldsDisabled ? 'action.hover' : 'transparent',
                          opacity: areChildFieldsDisabled ? 0.72 : 1,
                          transition: 'opacity 0.2s ease, background-color 0.2s ease',
                        }}
                      >
                        <FormControl fullWidth>
                          <InputLabel id={`meal-label-${child.child_id}`}>Masa</InputLabel>
                          <Select
                            labelId={`meal-label-${child.child_id}`}
                            value={reportData[child.child_id]?.meal || ''}
                            onChange={(e) =>
                              handleInputChange(child.child_id, 'meal', e.target.value)
                            }
                            disabled={isMealDisabled}
                            label="Masa"
                            displayEmpty
                          >
                            {todaysMeals.length === 0 ? (
                              <MenuItem value="">
                                <em>Nicio masă disponibilă</em>
                              </MenuItem>
                            ) : (
                              todaysMeals.map((option) => (
                                <MenuItem key={option.meal_id} value={option.meal_id}>
                                  {option.description}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel id={`eating-label-${child.child_id}`}>Cât a mâncat</InputLabel>
                          <Select
                            labelId={`eating-label-${child.child_id}`}
                            value={reportData[child.child_id]?.eating_amount || ''}
                            onChange={(e) =>
                              handleInputChange(child.child_id, 'eating_amount', e.target.value)
                            }
                            disabled={areNonMealFieldsDisabled}
                            label="Cât a mâncat"
                          >
                            <MenuItem value="foarte_bine">Foarte Bine</MenuItem>
                            <MenuItem value="bine">Bine</MenuItem>
                            <MenuItem value="satisfacator">Satisfăcător</MenuItem>
                            <MenuItem value="nesatisfacator">Nesatisfăcător</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel id={`behaviour-label-${child.child_id}`}>Comportament &amp; Dispoziție</InputLabel>
                          <Select
                            labelId={`behaviour-label-${child.child_id}`}
                            value={reportData[child.child_id]?.behaviour_mood || ''}
                            onChange={(e) =>
                              handleInputChange(child.child_id, 'behaviour_mood', e.target.value)
                            }
                            disabled={areNonMealFieldsDisabled}
                            label="Comportament & Dispoziție"
                          >
                            <MenuItem value="bine dispus">Bine dispus</MenuItem>
                            <MenuItem value="activ">Activ</MenuItem>
                            <MenuItem value="vesel">Vesel</MenuItem>
                            <MenuItem value="trist">Trist</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel id={`wc-label-${child.child_id}`}>Vizite la WC</InputLabel>
                          <Select
                            labelId={`wc-label-${child.child_id}`}
                            value={reportData[child.child_id]?.wc_visits || ''}
                            onChange={(e) =>
                              handleInputChange(child.child_id, 'wc_visits', e.target.value)
                            }
                            disabled={areNonMealFieldsDisabled}
                            label="Vizite la WC"
                          >
                            {[0, 1, 2, 3].map((num) => (
                              <MenuItem key={num} value={num}>
                                {num}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl fullWidth>
                          <InputLabel id={`sleep-label-${child.child_id}`}>Ore de Somn</InputLabel>
                          <Select
                            labelId={`sleep-label-${child.child_id}`}
                            value={reportData[child.child_id]?.sleep_hours || ''}
                            onChange={(e) =>
                              handleInputChange(child.child_id, 'sleep_hours', e.target.value)
                            }
                            disabled={areNonMealFieldsDisabled}
                            label="Ore de Somn"
                          >
                            {["30 m", "1 h", "1:30", "2 h", "2:30"].map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Note de Sănătate"
                          fullWidth
                          value={reportData[child.child_id]?.health_notes || ''}
                          onChange={(e) =>
                            handleInputChange(child.child_id, 'health_notes', e.target.value)
                          }
                          disabled={areNonMealFieldsDisabled}
                        />
                        <TextField
                          label="Evenimente Speciale"
                          fullWidth
                          value={reportData[child.child_id]?.special_events || ''}
                          onChange={(e) =>
                            handleInputChange(child.child_id, 'special_events', e.target.value)
                          }
                          disabled={areNonMealFieldsDisabled}
                        />
                        <TextField
                          label="Zone de Îngrijorare"
                          fullWidth
                          value={reportData[child.child_id]?.areas_of_concern || ''}
                          onChange={(e) =>
                            handleInputChange(child.child_id, 'areas_of_concern', e.target.value)
                          }
                          disabled={areNonMealFieldsDisabled}
                        />
                        <TextField
                          label="Atenție Părinți"
                          fullWidth
                          value={reportData[child.child_id]?.parent_attention || ''}
                          onChange={(e) =>
                            handleInputChange(child.child_id, 'parent_attention', e.target.value)
                          }
                          disabled={areNonMealFieldsDisabled}
                        />
                      </Box>
                      <Box mt={2}>
                        {!child.reportSent ? (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleSubmitReport(child)}
                          >
                            {childUiState.buttonLabel}
                          </Button>
                        ) : (
                          <Button variant="contained" disabled>
                            {childUiState.buttonLabel}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
              </Accordion>
            );
          })}
          {groupSendStatus[groupId]?.message && (
            <Alert severity={groupSendStatus[groupId].type} sx={{ mb: 2 }}>
              {groupSendStatus[groupId].message}
            </Alert>
          )}
          <Box display="flex" justifyContent="flex-end" mb={3}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleSendReports(groupId)}
              disabled={
                groupSendLoading[groupId] ||
                !groupData.children.some((child) => child.reportSent)
              }
            >
              {groupSendLoading[groupId] ? 'Se trimit rapoartele...' : 'Trimite Rapoartele'}
            </Button>
          </Box>
          <Divider style={{ margin: '20px 0' }} />
        </div>
      ))}
    </div>
  );
};

export default ReportsTable;
